import {
  ClaimOwnerCapacity,
  FieldCapacityByDay,
  FieldCapacityMode,
} from '@dispatch/shared/types';
import {
  Section,
  SectionDivider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@dispatch/web/ui';
import {
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactNode, useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
  useFormState,
  useWatch,
} from 'react-hook-form';

// The seven day-of-week capacity fields, in render order. Also the set re-seeded
// when a dispatcher first switches a resource from Static to Per day.
const FIELD_CAPACITY_DAY_NAMES = [
  'onSunday',
  'onMonday',
  'onTuesday',
  'onWednesday',
  'onThursday',
  'onFriday',
  'onSaturday',
] as const;

const styles = {
  columnLabel: {
    // Flush to the left margin (aligned with the section title), with extra
    // breathing room beneath before the day-of-week row.
    padding: '10px 0 0',
    marginBottom: '20px',
  },
  // Day-of-week inputs sit in a single row of seven narrow columns.
  dayRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 2,
    padding: '0 25px 0',
  },
  // Non-day-of-week inputs go on their own row beneath the day row.
  extraRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
    padding: '16px 25px 10px',
  },
  extraField: {
    width: '200px',
  },
  // Mode selector sits above the Handler label, since it decides what follows it.
  modeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    padding: '10px 0 0',
  },
};

export const ClaimCapacity = ({
  control,
  errors,
  claimOwnerCapacity,
  fieldCapacityByDay,
  isReadOnly = false,
  isFieldCapacityByDayEnabled = false,
  setValue,
}: {
  control: Control;
  errors: FieldErrors;
  claimOwnerCapacity?: ClaimOwnerCapacity;
  fieldCapacityByDay?: FieldCapacityByDay;
  isReadOnly?: boolean;
  /**
   * Needed only to pre-fill the seven day inputs when a dispatcher switches this
   * resource from Static to Per day. Omit it and the switch still works -- the grid
   * just keeps whatever was loaded.
   */
  setValue?: UseFormSetValue<FieldValues>;
  /**
   * Per-tenant kill-switch for the per-day field capacity model (ADO #94651).
   *
   * Two orthogonal switches decide what renders here. The tenant flag decides whether the
   * choice is offered at all; the per-resource `fieldCapacityMode` decides which capacity model
   * is live for this resource:
   *
   * - flag off -> no mode control, and the legacy single static Field input bound to
   *   `generalInformation.claimCapacity` (exactly today's behaviour).
   * - flag on + STATIC (or unset) -> the mode control plus that same static input, live.
   * - flag on + PER_DAY -> the mode control, the seven day-of-week inputs bound to
   *   `fieldCapacityByDay.*`, AND the static input -- captioned as the fallback it is.
   *
   * The static input renders in every case on purpose: the scalar and the per-day grid co-exist
   * server-side (nothing derives one from the other), and in PER_DAY mode the scalar is what this
   * resource resolves to if the tenant flag is switched off or it is moved back to Static. Hiding
   * it would leave a live value that a dispatcher can neither see nor edit here.
   *
   * The mode must be submitted, because the server resolves per-day capacity ONLY for resources
   * explicitly marked PER_DAY -- values written without it are stored but dormant.
   */
  isFieldCapacityByDayEnabled?: boolean;
}) => {
  // Watched rather than read once: switching the toggle has to swap the form immediately.
  const fieldCapacityMode = useWatch({
    control,
    name: 'fieldCapacityMode',
  }) as FieldCapacityMode | undefined;

  // An unset mode resolves as STATIC on the server, so it must render as STATIC here too --
  // showing the per-day grid for a resource that resolves from the scalar would be a lie.
  const isPerDay =
    isFieldCapacityByDayEnabled && fieldCapacityMode === 'PER_DAY';

  const [isFieldCapacityExpanded, setIsFieldCapacityExpanded] = useState(true);
  const [isClaimOwnerExpanded, setIsClaimOwnerExpanded] = useState(true);

  // Scoped to the day grid on purpose: an unscoped useFormState subscribes this component to
  // every field in the resource form, so it would re-render on each keystroke anywhere on the
  // General tab just to read one dirty flag.
  const { dirtyFields } = useFormState({ control, name: 'fieldCapacityByDay' });
  const staticFieldCapacity = useWatch({
    control,
    name: 'generalInformation.claimCapacity',
  });

  /**
   * Switching Static -> Per day starts the grid from the capacity the dispatcher can
   * currently see, rather than from whatever was loaded -- editing Field from 5 to 12
   * and then switching should offer 12 across all seven days.
   *
   * This is a one-way migration aid for a resource that has NO per-day configuration yet, so
   * it is gated on stored state, not on session state. Keying it off `dirtyFields` alone would
   * fire on a clean page load: a resource stored as 8/2/2/2/2/2/8 that a dispatcher toggles to
   * Static and straight back would have all seven days flattened to `claimCapacity` -- an
   * unrelated value (the resource's own static capacity, which is never derived from the grid),
   * so the weekend 8s would be silently destroyed and staged for save.
   *
   * `dirtyFields` is still checked so that per-day values entered *this* session (on a resource
   * that loaded without any) also survive a Per day -> Static -> Per day round trip.
   */
  const hasStoredPerDayValues = Boolean(fieldCapacityByDay);

  const seedDaysFromStaticValue = () => {
    if (
      !setValue ||
      hasStoredPerDayValues ||
      dirtyFields?.['fieldCapacityByDay']
    )
      return;
    const seed = Number(staticFieldCapacity) || 0;
    FIELD_CAPACITY_DAY_NAMES.forEach((day) => {
      setValue(`fieldCapacityByDay.${day}`, seed, { shouldDirty: true });
    });
  };

  const expandControl =
    (expanded: boolean, toggle: () => void, label: string) => (): ReactNode =>
      (
        <IconButton
          aria-label={label}
          aria-expanded={expanded}
          size="small"
          onClick={toggle}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      );

  return (
    <div data-testid="claimOwnerCapacity">
      <Section
        title="Field Capacity"
        rightTopControl={expandControl(
          isFieldCapacityExpanded,
          () => setIsFieldCapacityExpanded((prev) => !prev),
          'Toggle Field Capacity'
        )}
      >
        <Collapse in={isFieldCapacityExpanded}>
          {isFieldCapacityByDayEnabled && (
            <Box sx={styles.modeRow} data-testid="fieldCapacityModeControl">
              <Typography component="p" fontSize="14px">
                Field capacity
              </Typography>
              <Controller
                name="fieldCapacityMode"
                control={control}
                render={({ field }) => (
                  <ToggleButtonGroup
                    // This form is white, so the unselected button needs a dark
                    // label -- the group's default targets the dark quick-filter
                    // strip and would render it invisible here (ADO #94651).
                    surface="light"
                    // Controlled, not seeded: ResourcesDetails resets this form in an effect
                    // *after* this component mounts, so an uncontrolled group would keep its
                    // mount-time 'STATIC' seed and highlight the wrong button for every
                    // resource already stored as PER_DAY. Being controlled also means clicking
                    // the already-highlighted button still reports through onClick.
                    value={
                      (field.value as FieldCapacityMode | undefined) ?? 'STATIC'
                    }
                    onClick={(next) => {
                      if (next === 'PER_DAY' && field.value !== 'PER_DAY') {
                        seedDaysFromStaticValue();
                      }
                      field.onChange(next);
                    }}
                  >
                    <ToggleButton value="STATIC" disabled={isReadOnly}>
                      Static
                    </ToggleButton>
                    <ToggleButton value="PER_DAY" disabled={isReadOnly}>
                      Per day
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Box>
          )}
          <Typography component="p" fontSize="14px" sx={styles.columnLabel}>
            Handler
          </Typography>
          {isPerDay && (
            <Box sx={styles.dayRow}>
              <TextField
                id="fieldCapacityByDay.onSunday"
                name="fieldCapacityByDay.onSunday"
                label="Field Sunday"
                type="number"
                control={control}
                defaultValue={0}
                inputProps={{
                  readOnly: false,
                  startAdornment: (
                    <InputAdornment position="start">
                      ({fieldCapacityByDay?.initialOnSunday || 0})
                    </InputAdornment>
                  ),
                }}
                error={errors?.['fieldCapacityByDay']?.['onSunday']?.message}
                disabled={isReadOnly}
              />
              <TextField
                id="fieldCapacityByDay.onMonday"
                name="fieldCapacityByDay.onMonday"
                label="Field Monday"
                type="number"
                control={control}
                defaultValue={0}
                inputProps={{
                  readOnly: false,
                  startAdornment: (
                    <InputAdornment position="start">
                      ({fieldCapacityByDay?.initialOnMonday || 0})
                    </InputAdornment>
                  ),
                }}
                error={errors?.['fieldCapacityByDay']?.['onMonday']?.message}
                disabled={isReadOnly}
              />
              <TextField
                id="fieldCapacityByDay.onTuesday"
                name="fieldCapacityByDay.onTuesday"
                label="Field Tuesday"
                type="number"
                control={control}
                defaultValue={0}
                inputProps={{
                  readOnly: false,
                  startAdornment: (
                    <InputAdornment position="start">
                      ({fieldCapacityByDay?.initialOnTuesday || 0})
                    </InputAdornment>
                  ),
                }}
                error={errors?.['fieldCapacityByDay']?.['onTuesday']?.message}
                disabled={isReadOnly}
              />
              <TextField
                id="fieldCapacityByDay.onWednesday"
                name="fieldCapacityByDay.onWednesday"
                label="Field Wednesday"
                type="number"
                control={control}
                defaultValue={0}
                inputProps={{
                  readOnly: false,
                  startAdornment: (
                    <InputAdornment position="start">
                      ({fieldCapacityByDay?.initialOnWednesday || 0})
                    </InputAdornment>
                  ),
                }}
                error={errors?.['fieldCapacityByDay']?.['onWednesday']?.message}
                disabled={isReadOnly}
              />
              <TextField
                id="fieldCapacityByDay.onThursday"
                name="fieldCapacityByDay.onThursday"
                label="Field Thursday"
                type="number"
                control={control}
                defaultValue={0}
                inputProps={{
                  readOnly: false,
                  startAdornment: (
                    <InputAdornment position="start">
                      ({fieldCapacityByDay?.initialOnThursday || 0})
                    </InputAdornment>
                  ),
                }}
                error={errors?.['fieldCapacityByDay']?.['onThursday']?.message}
                disabled={isReadOnly}
              />
              <TextField
                id="fieldCapacityByDay.onFriday"
                name="fieldCapacityByDay.onFriday"
                label="Field Friday"
                type="number"
                control={control}
                defaultValue={0}
                inputProps={{
                  readOnly: false,
                  startAdornment: (
                    <InputAdornment position="start">
                      ({fieldCapacityByDay?.initialOnFriday || 0})
                    </InputAdornment>
                  ),
                }}
                error={errors?.['fieldCapacityByDay']?.['onFriday']?.message}
                disabled={isReadOnly}
              />
              <TextField
                id="fieldCapacityByDay.onSaturday"
                name="fieldCapacityByDay.onSaturday"
                label="Field Saturday"
                type="number"
                control={control}
                defaultValue={0}
                inputProps={{
                  readOnly: false,
                  startAdornment: (
                    <InputAdornment position="start">
                      ({fieldCapacityByDay?.initialOnSaturday || 0})
                    </InputAdornment>
                  ),
                }}
                error={errors?.['fieldCapacityByDay']?.['onSaturday']?.message}
                disabled={isReadOnly}
              />
            </Box>
          )}
          <Box sx={styles.extraRow}>
            {/*
             * Rendered in BOTH modes. The static scalar and the per-day grid co-exist on a
             * resource -- the server stores both and picks between them at resolve time from the
             * tenant flag and fieldCapacityMode -- so in per-day mode this is the resource's
             * fallback capacity: what it resolves to if per-day is switched off for the tenant or
             * the resource is moved back to Static. Hiding it would leave a dispatcher with a live
             * value they can neither see nor edit here (bulk update writes it too).
             */}
            <Box sx={styles.extraField}>
              <TextField
                id="claimCapacity"
                name="generalInformation.claimCapacity"
                label="Field"
                type="number"
                control={control}
                defaultValue={0}
                inputProps={{
                  readOnly: false,
                }}
                error={
                  errors?.['generalInformation']?.['claimCapacity']?.message
                }
                disabled={isReadOnly}
              />
              {isPerDay && (
                <Typography
                  component="p"
                  fontSize="12px"
                  data-testid="static-field-capacity-fallback-note"
                >
                  Used if per-day capacity is turned off.
                </Typography>
              )}
            </Box>
            <Box sx={styles.extraField}>
              <TextField
                id="virtualClaimCapacity"
                name="generalInformation.virtualClaimCapacity"
                label="Virtual"
                type="number"
                control={control}
                inputProps={{
                  readOnly: false,
                }}
                error={
                  errors?.['generalInformation']?.['virtualClaimCapacity']
                    ?.message
                }
                disabled={isReadOnly}
              />
            </Box>
            <Box sx={styles.extraField}>
              <TextField
                id="complexLossClaimCapacity"
                name="generalInformation.complexLossClaimCapacity"
                label="Complex Loss - Weekly"
                type="number"
                control={control}
                inputProps={{
                  readOnly: false,
                }}
                error={
                  errors?.['generalInformation']?.['complexLossClaimCapacity']
                    ?.message
                }
                disabled={isReadOnly}
              />
            </Box>
          </Box>
        </Collapse>
      </Section>

      <SectionDivider />

      <Section
        title="Claim Owner Capacity"
        rightTopControl={expandControl(
          isClaimOwnerExpanded,
          () => setIsClaimOwnerExpanded((prev) => !prev),
          'Toggle Claim Owner Capacity'
        )}
      >
        <Collapse in={isClaimOwnerExpanded}>
          <Typography component="p" fontSize="14px" sx={styles.columnLabel}>
            Owner
          </Typography>
          <Box sx={styles.dayRow}>
            <TextField
              id="claimOwner.onSunday"
              name="claimOwner.onSunday"
              label="Sunday"
              type="number"
              control={control}
              defaultValue={0}
              inputProps={{
                readOnly: false,
                startAdornment: (
                  <InputAdornment position="start">
                    ({claimOwnerCapacity?.initialOnSunday || 0})
                  </InputAdornment>
                ),
              }}
              error={errors?.['claimOwner']?.['onSunday']?.message}
              disabled={isReadOnly}
            />
            <TextField
              id="claimOwner.onMonday"
              name="claimOwner.onMonday"
              label="Monday"
              type="number"
              control={control}
              defaultValue={0}
              inputProps={{
                readOnly: false,
                startAdornment: (
                  <InputAdornment position="start">
                    ({claimOwnerCapacity?.initialOnMonday || 0})
                  </InputAdornment>
                ),
              }}
              error={errors?.['claimOwner']?.['onMonday']?.message}
              disabled={isReadOnly}
            />
            <TextField
              id="claimOwner.onTuesday"
              name="claimOwner.onTuesday"
              label="Tuesday"
              type="number"
              control={control}
              defaultValue={0}
              inputProps={{
                readOnly: false,
                startAdornment: (
                  <InputAdornment position="start">
                    ({claimOwnerCapacity?.initialOnTuesday || 0})
                  </InputAdornment>
                ),
              }}
              error={errors?.['claimOwner']?.['onTuesday']?.message}
              disabled={isReadOnly}
            />
            <TextField
              id="claimOwner.onWednesday"
              name="claimOwner.onWednesday"
              label="Wednesday"
              type="number"
              control={control}
              defaultValue={0}
              inputProps={{
                readOnly: false,
                startAdornment: (
                  <InputAdornment position="start">
                    ({claimOwnerCapacity?.initialOnWednesday || 0})
                  </InputAdornment>
                ),
              }}
              error={errors?.['claimOwner']?.['onWednesday']?.message}
              disabled={isReadOnly}
            />
            <TextField
              id="claimOwner.onThursday"
              name="claimOwner.onThursday"
              label="Thursday"
              type="number"
              control={control}
              defaultValue={0}
              inputProps={{
                readOnly: false,
                startAdornment: (
                  <InputAdornment position="start">
                    ({claimOwnerCapacity?.initialOnThursday || 0})
                  </InputAdornment>
                ),
              }}
              error={errors?.['claimOwner']?.['onThursday']?.message}
              disabled={isReadOnly}
            />
            <TextField
              id="claimOwner.onFriday"
              name="claimOwner.onFriday"
              label="Friday"
              type="number"
              control={control}
              defaultValue={0}
              inputProps={{
                readOnly: false,
                startAdornment: (
                  <InputAdornment position="start">
                    ({claimOwnerCapacity?.initialOnFriday || 0})
                  </InputAdornment>
                ),
              }}
              error={errors?.['claimOwner']?.['onFriday']?.message}
              disabled={isReadOnly}
            />
            <TextField
              id="claimOwner.onSaturday"
              name="claimOwner.onSaturday"
              label="Saturday"
              type="number"
              control={control}
              defaultValue={0}
              inputProps={{
                readOnly: false,
                startAdornment: (
                  <InputAdornment position="start">
                    ({claimOwnerCapacity?.initialOnSaturday || 0})
                  </InputAdornment>
                ),
              }}
              disabled={isReadOnly}
              error={errors?.['claimOwner']?.['onSaturday']?.message}
            />
          </Box>
          <Box sx={styles.extraRow}>
            <Box sx={styles.extraField}>
              <TextField
                id="claimOwner.maxWeeklyCapacity"
                name="claimOwner.maxWeeklyCapacity"
                label="Max Weekly Capacity"
                type="number"
                control={control}
                defaultValue={0}
                error={errors?.['claimOwner']?.['maxWeeklyCapacity']?.message}
                disabled={isReadOnly}
                inputProps={{
                  readOnly: false,
                  startAdornment: (
                    <InputAdornment position="start">
                      ({claimOwnerCapacity?.initialMaxWeeklyCapacity || 0})
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        </Collapse>
      </Section>
    </div>
  );
};
